/**
 * Analytics data layer (FEAT-ANALYTICS-001). Read-only GETs to RRS /analytics/*,
 * which proxies to the Event service's /v2/analytics/* reports over the EXISTING
 * event + session data. Uses the portal-wide axios + getApiHeaders convention
 * (x-api-key + x-access-token) — the same as Boards/MetricTree. Not the Smart
 * Links RTK/mock layer, and nothing to do with Boards.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getApiHeaders } from '../../utils/DefaultFunction';

const BASE = process.env.REACT_APP_API_URL || '';

// NOTE: conversions/key-events are defined ONLY by the tenant's declared Conversion
// Events (RRS injects ?conv= on every analytics call). The former hardcoded
// CONVERSION_EVENTS ?events= list was removed — it over-counted and made Snapshot /
// Converting-sources disagree with the Conversions page (DA-BUG-0451).

/** GET /analytics/<report> with the current time window params. */
export async function fetchReport(report, params = {}) {
  const res = await axios.get(`${BASE}/analytics/${report}`, {
    params,
    headers: getApiHeaders(),
    timeout: 130000,
  });
  return res.data;
}

/** GET /analytics/properties — the tenant's GA-style properties for the selector.
 *  Derived from observed data (Mobile App, each web domain, API, Messaging). Safe
 *  [{all}] fallback on error so the selector always renders. */
export async function fetchProperties() {
  try {
    const res = await axios.get(`${BASE}/analytics/properties`, { headers: getApiHeaders(), timeout: 30000 });
    const list = res?.data?.properties;
    return Array.isArray(list) && list.length ? list : null;
  } catch (_) {
    return null;
  }
}

/**
 * Hook: fetch one report, re-fetching whenever the serialized params change.
 * Returns { data, loading, error, reload }.
 */
export function useReport(report, params) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const key = JSON.stringify(params || {});
  const reqId = useRef(0);

  const load = () => {
    const id = ++reqId.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchReport(report, params)
      .then((data) => {
        if (id === reqId.current) setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (id === reqId.current) {
          const msg = err?.response?.data?.message || err.message || 'Failed to load';
          setState({ data: null, loading: false, error: msg });
        }
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [report, key]);

  return { ...state, reload: load };
}

/**
 * Distinct values for a dimension (for the Audience-builder dropdowns). Results
 * are cached per-dimension for the session so reopening the picker is instant;
 * `enumerable:false` dims (numeric/time) resolve to an empty list. Never throws.
 */
const _valueCache = new Map();
export async function fetchDimensionValues(dim) {
  if (!dim) return { enumerable: false, values: [] };
  if (_valueCache.has(dim)) return _valueCache.get(dim);
  try {
    const res = await axios.get(`${BASE}/analytics/values`, {
      params: { dim, limit: 200 }, headers: getApiHeaders(), timeout: 60000,
    });
    const out = { enumerable: !!res.data?.enumerable, values: res.data?.values || [] };
    _valueCache.set(dim, out);
    return out;
  } catch (_) {
    return { enumerable: false, values: [] };
  }
}

/** Hook: dimension value list for a dropdown. { values, enumerable, loading }. */
export function useDimensionValues(dim) {
  const [state, setState] = useState({ values: [], enumerable: false, loading: !!dim });
  useEffect(() => {
    let alive = true;
    if (!dim) { setState({ values: [], enumerable: false, loading: false }); return; }
    setState((s) => ({ ...s, loading: true }));
    fetchDimensionValues(dim).then((r) => { if (alive) setState({ ...r, loading: false }); });
    return () => { alive = false; };
  }, [dim]);
  return state;
}

/** Build the window params the API expects from the toolbar state. */
export function windowParams({ mode, range, from, to }) {
  if (mode === 'custom' && from && to) return { from, to };
  return { range: range || 'last_30_days' };
}
